/**
 * useSidebarState — localStorage-backed expand/collapse persistence
 * for sidebar nav groups.
 *
 * NAV-2-5: persists per-workspace so switching workspaces restores
 * that workspace's last-known sidebar layout.
 *
 * Storage key: `gacpro:sidebar:${workspaceSlug}`
 * Value: JSON array of expanded group ids.
 *
 * All localStorage access is wrapped in try/catch so the hook
 * degrades gracefully in private browsing or when storage is full.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { NavGroup, NavTopLevel } from './nav-config';

export interface SidebarState {
  expandedGroups: Set<string>;
  toggleGroup: (groupId: string) => void;
  isExpanded: (groupId: string) => boolean;
}

function storageKey(slug: string): string {
  return `gacpro:sidebar:${slug}`;
}

function readFromStorage(slug: string): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey(slug));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
      return parsed as string[];
    }
    return null;
  } catch {
    return null;
  }
}

function writeToStorage(slug: string, expandedIds: string[]): void {
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(expandedIds));
  } catch {
    // Storage full or blocked — silently degrade.
  }
}

/**
 * Compute default expanded set: the "overview" group (always
 * defaultExpanded in nav-config) plus the group containing the
 * currently active item.
 */
function defaultExpanded(
  groups: (NavGroup | NavTopLevel)[],
  activeGroupId: string | null,
): Set<string> {
  const set = new Set<string>();
  for (const entry of groups) {
    if ('topLevel' in entry && entry.topLevel) continue;
    const group = entry as NavGroup;
    if (group.defaultExpanded) set.add(group.id);
  }
  if (activeGroupId) set.add(activeGroupId);
  return set;
}

export function useSidebarState(
  workspaceSlug: string,
  navGroups: (NavGroup | NavTopLevel)[],
  activeGroupId: string | null,
): SidebarState {
  // Use a ref to track the slug we last loaded for, so we can detect
  // workspace switches and reload from storage.
  const loadedSlugRef = useRef<string>('');

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    // Initial state: try reading from storage, else compute defaults.
    const saved = readFromStorage(workspaceSlug);
    if (saved) {
      const set = new Set(saved);
      // Auto-expand the active group even if it was previously collapsed.
      if (activeGroupId) set.add(activeGroupId);
      return set;
    }
    return defaultExpanded(navGroups, activeGroupId);
  });

  // When the workspace slug changes, reload from that workspace's storage.
  useEffect(() => {
    if (loadedSlugRef.current === workspaceSlug) return;
    loadedSlugRef.current = workspaceSlug;

    const saved = readFromStorage(workspaceSlug);
    if (saved) {
      const set = new Set(saved);
      if (activeGroupId) set.add(activeGroupId);
      setExpandedGroups(set);
    } else {
      setExpandedGroups(defaultExpanded(navGroups, activeGroupId));
    }
  }, [workspaceSlug, navGroups, activeGroupId]);

  // When the active group changes (user navigated), auto-expand it.
  const prevActiveRef = useRef(activeGroupId);
  useEffect(() => {
    if (activeGroupId && activeGroupId !== prevActiveRef.current) {
      prevActiveRef.current = activeGroupId;
      setExpandedGroups((prev) => {
        if (prev.has(activeGroupId)) return prev;
        const next = new Set(prev);
        next.add(activeGroupId);
        writeToStorage(workspaceSlug, Array.from(next));
        return next;
      });
    }
  }, [activeGroupId, workspaceSlug]);

  const toggleGroup = useCallback(
    (groupId: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        writeToStorage(workspaceSlug, Array.from(next));
        return next;
      });
    },
    [workspaceSlug],
  );

  const isExpanded = useCallback(
    (groupId: string) => expandedGroups.has(groupId),
    [expandedGroups],
  );

  return { expandedGroups, toggleGroup, isExpanded };
}
